/**
 * Страница игрока в статистике: цифры с vzp-gta5rp.com и история боёв.
 *
 * Открывается по нажатию на игрока во вкладке «Статистика». Цифры — по
 * одному серверу (Mirror и Blaine не смешиваются); сервер берётся из того
 * же выбора, что и на странице статистики, и тут же переключается.
 *
 * Убийства, смерти, K/D, HS% и МП сайт считает сам по всем боям игрока на
 * сервере. История ниже — только бои семьи, записанные ботом.
 */

import { useState, type ReactNode } from 'react';

import { formatDateTime } from '../lib/format.ts';
import { navigate, useLinkHandler } from '../lib/router.ts';
import { useResource } from '../lib/useResource.ts';
import { Button, EmptyState, ErrorState, Panel, SegmentedControl, Skeleton } from '../components/ui.tsx';
import {
  captureOutcome,
  formatKd,
  OUTCOME_LABEL,
  outcomeColor,
  playerName,
  readSavedServer,
  saveServer,
  winRate,
  type FamilySummary,
  type StatsPlayer,
} from '../lib/stats.ts';

interface PlayerCapture {
  captureId: number;
  familyId: number;
  startedAt: string;
  defendersId: number;
  attackersId: number;
  defendersName: string | null;
  attackersName: string | null;
  winner: number | null;
  point: string | null;
  damage: number;
  kills: number;
  shots: number | null;
}

interface PlayerStatsResponse {
  staticId: string;
  player: StatsPlayer | null;
  place: number | null;
  familySize: number;
  captures: PlayerCapture[];
  family: FamilySummary;
  servers: string[];
  server: string | null;
}

const number = (value: number): string => Math.round(value).toLocaleString('ru');
const percent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${value.toFixed(1)}%`;

/** Подпись мелкими заглавными — как у всех разделов страницы. */
function Caption({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="text-[0.65rem] tracking-[0.2em] uppercase" style={{ color: 'var(--text-muted)' }}>
      {children}
    </div>
  );
}

/** Плашка у имени: место, семья, тир. */
function Chip({ children }: { children: ReactNode }): ReactNode {
  return (
    <span
      className="rounded-md px-2 py-0.5 text-xs font-medium"
      style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)' }}
    >
      {children}
    </span>
  );
}

/** Крупное число в шапке. */
function HeroNumber({ value, label }: { value: string; label: string }): ReactNode {
  return (
    <div className="text-center">
      <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-rounded)' }}>
        {value}
      </div>
      <Caption>{label}</Caption>
    </div>
  );
}

/** Карточка строк «название — число». */
function StatList({ title, rows, footer }: { title: string; rows: [string, string][]; footer?: ReactNode }): ReactNode {
  return (
    <div
      className="rounded-xl p-5"
      style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)' }}
    >
      <Caption>{title}</Caption>
      <dl className="mt-3">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between py-2 text-sm"
            style={{ borderBottom: '1px solid var(--border-subtle)' }}
          >
            <dt style={{ color: 'var(--text-muted)' }}>{label}</dt>
            <dd className="font-semibold tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {footer}
    </div>
  );
}

/** Знак игрока: аватарка из Discord, а без неё — первая буква ника. */
function Avatar({ player, name }: { player: StatsPlayer | null; name: string }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="relative grid size-20 shrink-0 place-items-center overflow-hidden rounded-full text-3xl font-semibold"
      style={{
        border: '2px solid var(--border-strong, var(--border-subtle))',
        background: 'var(--surface-raised)',
        fontFamily: 'var(--font-rounded)',
      }}
    >
      {name.slice(0, 1).toUpperCase()}
      {player?.avatarUrl != null && (
        <img src={player.avatarUrl} alt="" className="absolute inset-0 size-full object-cover" />
      )}
    </span>
  );
}

/** Строка истории: один бой игрока. */
function HistoryRow({ capture, timezone }: { capture: PlayerCapture; timezone: string }): ReactNode {
  const onLinkClick = useLinkHandler();
  const outcome = captureOutcome(capture);
  const color = outcomeColor(outcome);
  const weDefended = capture.defendersId === capture.familyId;
  const opponent = (weDefended ? capture.attackersName : capture.defendersName) ?? 'противник';
  const sideColor = weDefended ? 'var(--text-primary)' : 'var(--positive)';

  return (
    <a
      href={`/stats/captures/${String(capture.captureId)}`}
      onClick={onLinkClick}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl px-5 py-3.5 transition-colors hover:bg-[var(--surface-hover)]"
      style={{ border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)' }}
    >
      <span className="w-28 shrink-0 text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {formatDateTime(capture.startedAt, timezone)}
      </span>
      <span className="min-w-[8rem] flex-1 font-semibold">vs {opponent}</span>

      <span
        className="rounded-md px-2 py-0.5 text-[0.65rem] font-bold tracking-wide"
        style={{ color: sideColor, border: `1px solid color-mix(in oklab, ${sideColor} 45%, transparent)` }}
      >
        {weDefended ? 'DEF' : 'ATK'}
      </span>
      {capture.point !== null && (
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {capture.point}
        </span>
      )}

      <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
        убийства <b style={{ color: 'var(--text-primary)' }}>{capture.kills}</b> / урон{' '}
        <b style={{ color: 'var(--text-primary)' }}>{number(capture.damage)}</b>
        {capture.shots !== null && (
          <>
            {' '}
            / выстрелы <b style={{ color: 'var(--text-primary)' }}>{capture.shots}</b>
          </>
        )}
      </span>

      <span
        className="w-24 rounded-md px-2 py-1 text-center text-xs font-semibold"
        style={{ color, border: `1px solid color-mix(in oklab, ${color} 45%, transparent)` }}
      >
        {OUTCOME_LABEL[outcome]}
      </span>
    </a>
  );
}

export function StaticCardPage({ staticId, timezone }: { staticId: string; timezone: string }): ReactNode {
  const [server, setServer] = useState<string>(readSavedServer);
  const query = server === '' ? '' : `?server=${encodeURIComponent(server)}`;
  const { data, loading, error, reload } = useResource<PlayerStatsResponse>(
    `/api/stats/statics/${encodeURIComponent(staticId)}${query}`,
  );

  const chooseServer = (next: string): void => {
    setServer(next);
    saveServer(next);
  };

  const back = (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          navigate('/stats');
        }}
      >
        ← К статистике
      </Button>
      {data !== null && data.servers.length > 1 && data.server !== null && (
        <SegmentedControl<string>
          label="Сервер"
          value={data.server}
          onChange={chooseServer}
          options={data.servers.map((name) => ({ value: name, label: name }))}
        />
      )}
    </div>
  );

  if (error !== null) {
    return (
      <>
        {back}
        <ErrorState message={error} onRetry={reload} />
        {/* Игрока нет на этом сервере — пусть можно будет переключиться обратно. */}
        {server !== '' && (
          <div className="mt-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                chooseServer('');
              }}
            >
              Показать сервер по умолчанию
            </Button>
          </div>
        )}
      </>
    );
  }

  if (loading || data === null) {
    return <Skeleton rows={6} />;
  }

  const player = data.player;
  const name = player === null ? staticId.replace(/^n_/, '') : playerName(player);
  const rate = player === null ? null : winRate(player);
  const hasSite = player?.kd !== null && player?.kd !== undefined;

  return (
    <>
      {back}

      <Panel className="mb-8 p-6">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex min-w-0 flex-1 items-center gap-5">
            <Avatar player={player} name={name} />
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-bold" style={{ fontFamily: 'var(--font-rounded)' }}>
                {name}
              </h1>
              {data.server !== null && <Caption>{data.server}</Caption>}
              <div className="mt-2 flex flex-wrap gap-2">
                {data.place !== null && (
                  <Chip>
                    #{data.place} в семье из {data.familySize}
                  </Chip>
                )}
                {data.family.name !== null && <Chip>{data.family.name}</Chip>}
                {player?.discordName != null && <Chip>{player.discordName}</Chip>}
              </div>
            </div>
          </div>

          <div className="flex gap-8">
            <HeroNumber value={player === null ? '—' : number(player.kills)} label="kills" />
            <HeroNumber value={player === null ? '—' : formatKd(player)} label="k/d" />
            <HeroNumber value={rate === null ? '—' : `${(rate * 100).toFixed(1)}%`} label="winrate" />
          </div>
        </div>

        {player !== null && (
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <StatList
              title="Боевая статистика"
              rows={[
                ['Убийства', number(player.kills)],
                ['Смерти', player.deaths == null ? '—' : number(player.deaths)],
                ['K/D', formatKd(player)],
                ['Урон', number(player.damage)],
                ['HS%', percent(player.hsPercent)],
              ]}
            />
            <StatList
              title="Мероприятия"
              rows={[
                ['Всего МП', number(player.captures)],
                ['Побед', number(player.wins)],
                ['Винрейт', rate === null ? '—' : `${(rate * 100).toFixed(1)}%`],
                ['Хитрейт', percent(player.hitPercent)],
              ]}
              footer={
                rate !== null && (
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full" style={{ background: 'var(--surface-hover)' }}>
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${String(rate * 100)}%`, background: 'var(--positive)' }}
                    />
                  </div>
                )
              }
            />
          </div>
        )}

        {player !== null && !hasSite && (
          <p className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
            Полные цифры с vzp-gta5rp.com ещё не подтянулись — пока показаны бои, записанные ботом. Обновление раз в
            5 минут.
          </p>
        )}
      </Panel>

      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-sm font-semibold tracking-[0.2em] uppercase">История участий</h2>
        <span className="h-px flex-1" style={{ background: 'var(--border-subtle)' }} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {data.captures.length}
        </span>
      </div>

      {data.captures.length === 0 ? (
        <Panel>
          <EmptyState title="Боёв нет" description="На этом сервере бот ещё не записал боёв игрока." />
        </Panel>
      ) : (
        <div className="flex flex-col gap-2">
          {data.captures.map((capture) => (
            <HistoryRow key={capture.captureId} capture={capture} timezone={timezone} />
          ))}
        </div>
      )}
    </>
  );
}
